import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lightning, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

const SPORTS = [
  { key: "nfl", name: "NFL", img: "https://images.unsplash.com/photo-1544765773-a8dce1f272f6?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { key: "nba", name: "NBA", img: "https://images.unsplash.com/photo-1608245449230-4ac19066d2d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { key: "mlb", name: "MLB", img: "https://images.unsplash.com/photo-1529768167801-9173d94c2a42?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { key: "nhl", name: "NHL", img: "https://images.unsplash.com/flagged/photo-1550585477-a025700d7fce?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
];

const RISKS = [
  { key: "safe", label: "Safe", color: "#00E676", desc: "High-probability legs, steady payouts" },
  { key: "medium", label: "Medium", color: "#F59E0B", desc: "Balanced risk and reward" },
  { key: "lotto", label: "Lotto", color: "#FF3366", desc: "Long shots, big payout swings" },
];

export default function Onboarding() {
  const { user, setUser, API } = useAuth();
  const navigate = useNavigate();
  const [sport, setSport] = useState(user?.favorite_sport || null);
  const [risk, setRisk] = useState(user?.risk_preference || null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/auth/preferences`,
        { favorite_sport: sport, risk_preference: risk }, { withCredentials: true });
      setUser(data);
      navigate("/app");
    } catch {
      toast.error("Couldn't save preferences");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-slate-50 noise-bg flex flex-col">
      <main className="relative z-10 flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-14">
        <div className="flex items-center gap-2 mb-10">
          <Lightning size={24} weight="fill" className="text-[#00E676]" />
          <span className="font-display text-xl font-black uppercase tracking-tight">Parlay<span className="text-[#00E676]">Labs</span></span>
        </div>
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#00E676] mb-2">Quick setup · 10 seconds</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter mb-10">
          Tune your builder
        </h1>

        <section className="mb-10">
          <h2 className="text-base font-bold uppercase tracking-wide text-slate-300 mb-4">What's your sport?</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {SPORTS.map((s) => (
              <button key={s.key} onClick={() => setSport(s.key)} data-testid={`onboard-sport-${s.key}`}
                className={`relative h-24 rounded-xl overflow-hidden text-left transition-transform hover:scale-[1.03] active:scale-95 ${
                  sport === s.key ? "ring-2 ring-[#00E676]" : "ring-1 ring-white/10"}`}>
                <img src={s.img} alt={s.name} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                <span className="absolute bottom-2 left-3 font-display text-xl font-black uppercase">{s.name}</span>
                {sport === s.key && <span className="absolute top-2 right-2.5 h-2.5 w-2.5 rounded-full bg-[#00E676] drop-shadow-[0_0_6px_rgba(0,230,118,0.8)]" />}
              </button>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-base font-bold uppercase tracking-wide text-slate-300 mb-4">How do you like your risk?</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            {RISKS.map((r) => (
              <button key={r.key} onClick={() => setRisk(r.key)} data-testid={`onboard-risk-${r.key}`}
                className={`rounded-xl border p-4 text-left transition-colors hover:bg-white/5 ${
                  risk === r.key ? "bg-white/5" : "bg-[#141419]"}`}
                style={{ borderColor: risk === r.key ? r.color : "rgba(255,255,255,0.1)" }}>
                <p className="font-display text-2xl font-black uppercase tracking-tight" style={{ color: r.color }}>{r.label}</p>
                <p className="text-xs text-slate-400 mt-1">{r.desc}</p>
              </button>
            ))}
          </div>
        </section>

        <div className="flex items-center gap-4">
          <button onClick={save} disabled={!sport || !risk || busy} data-testid="onboard-save-btn"
            className="rounded-full bg-[#00E676] text-black font-bold px-8 py-3.5 text-sm hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-40 inline-flex items-center gap-2">
            {busy ? "Saving..." : "Start building"} <ArrowRight size={18} weight="bold" />
          </button>
          <button onClick={() => navigate("/app")} data-testid="onboard-skip-btn"
            className="text-sm font-semibold text-slate-500 hover:text-white transition-colors">
            Skip for now
          </button>
        </div>
      </main>
    </div>
  );
}
